import UseAxios from '../../../Api/http/http';
import { certificationList } from '../../../Api/request/certification';
import { verticalData } from '../../../Api/request/master';
import { addTargetData, editTargetData } from '../../../Api/request/target';
import { STATUS_CODES } from '../../../Api/resources/statusCodes';
import { EDIT_TARGET, SET_TARGET } from '../../../Constants/strings';

export const getCurrentYear = () => new Date().getFullYear();
export const years = Array.from(
  { length: 4 },
  (year, index) => getCurrentYear() + index,
);

export const generateNextYears = (targetSetYears) => {
  const currentYear = new Date().getFullYear();
  const targetYearsSet = new Set(targetSetYears.map(Number));
  const newYears = [];

  // Start from the current year and find the next 4 years not in targetYearsSet
  for (let year = currentYear; year < currentYear + 4; year++) {
    if (!targetYearsSet.has(year)) {
      newYears.push(year);
    }
  }

  return newYears;
};

export const initialQuarters = Array.from({ length: 4 }, (quarter, index) => ({
  quarter: index + 1,
  targets: [],
}));

export const fetchCertifications = async (snackbarOpen) => {
  const certResponse = await UseAxios(certificationList());
  const certResponseStatus = certResponse.data?.response?.data.statusCode;
  if (certResponseStatus === STATUS_CODES.NO_DATA) {
    snackbarOpen(`${SET_TARGET.CERTIFICATION_NO_DATA}`, 'warning');
    return;
  }
  return certResponse.data.certificationDetails
    .filter((cert) => cert.isActive)
    .map((cert) => ({
      id: cert.certificationId,
      name: cert.certificationName,
    }));
};

export const fetchVerticals = async () => {
  const verticalResponse = await UseAxios(verticalData());
  return verticalResponse.data.verticalDetails.map((vert) => ({
    id: vert.id,
    name: vert.verticalName,
  }));
};

export const createInitialTargets = (verts) => {
  return verts.reduce((acc, vert) => {
    acc[vert.name] = initialQuarters.map((quarter) => ({
      ...quarter,
      targets: [],
    }));
    return acc;
  }, {});
};

export const populateInitialTargets = (
  targets,
  initialTargets,
  certs,
  verts,
) => {
  targets.forEach((target) => {
    const vert = verts.find((vertical) => vertical.id === target.verticalId); // Adjust for new data structure
    if (vert) {
      const quarter = initialTargets[vert.name].find(
        (quarts) => quarts.quarter === target.quarter,
      );
      if (quarter) {
        quarter.targets.push({
          targetId: target.targetId,
          certification: {
            id: target.certificationsId,
            name: certs.find(
              (certification) => certification.id === target.certificationsId,
            )?.name,
          },
          target: target.currentQuarterTarget,
          targetDetails: target,
          isUpdated: false,
        });
      }
    }
  });
};

export const processQuarterTargets = async (params) => {
  const {
    vert,
    quarter,
    year,
    certifications,
    isEdit,
    snackbarOpen,
    navigate,
    pendingYear,
    userProfile,
    setIsErrorMsg,
  } = params;
  const saveTargetPromises = quarter.targets
    .filter((target) => target.isUpdated) // Only process updated targets
    .map(async (target) => {
      const certId = certifications.find(
        (cert) => cert.name === target.certification.name,
      )?.id;
      if (certId) {
        await saveTarget({
          targetId: target.targetId, // Include targetId for editing if present
          vertId: vert.id,
          certId,
          target: target.target,
          year,
          quarter: quarter.quarter,
          isEdit,
          snackbarOpen,
          navigate,
          pendingYear,
          userProfile,
          setIsErrorMsg,
        });
      }
    });
  await Promise.all(saveTargetPromises);
};

export const processVerticalTargets = async (params) => {
  const {
    vert,
    verticalTargets,
    year,
    isEdit,
    certifications,
    snackbarOpen,
    navigate,
    pendingYear,
    userProfile,
    setIsErrorMsg,
  } = params;
  for (const quarter of verticalTargets) {
    await processQuarterTargets({
      vert,
      quarter,
      year,
      certifications,
      isEdit,
      snackbarOpen,
      navigate,
      pendingYear,
      userProfile,
      setIsErrorMsg,
    });
  }
};
const saveTarget = async ({
  targetId,
  vertId,
  certId,
  target,
  year,
  quarter,
  isEdit,
  snackbarOpen,
  navigate,
  pendingYear,
  userProfile,
  setIsErrorMsg,
}) => {
  try {
    let response = null;
    if (isEdit && targetId) {
      response = await UseAxios(
        editTargetData(
          targetId,
          vertId,
          certId,
          target,
          year,
          quarter,
          userProfile.user_name,
        ),
      );
    } else {
      response = await UseAxios(
        addTargetData(
          vertId,
          certId,
          target,
          year,
          quarter,
          userProfile.user_name,
        ),
      );
    }
    if (response.data.status === 200 || response.data.statusCode === 200) {
      snackbarOpen(
        isEdit ? `${EDIT_TARGET.TARGET_UPDATED}` : `${SET_TARGET.TARGET_SET}`,
        'success',
      );
      if (pendingYear == null) {
        navigate('/certifications/target');
      }
    }
    if (response.data.status === 500 || response.data.statusCode === 500) {
      snackbarOpen(`${SET_TARGET.OOPS_TRY_AGAIN}`, 'warning');
      setIsErrorMsg(false);
    }
  } catch (error) {
    console.error('Error saving target:', error);
    snackbarOpen(`${SET_TARGET.OOPS_TRY_AGAIN}`, 'warning');
    setIsErrorMsg(false);
  }
};

export const handleClickOutside = (
  event,
  targetSetterRef,
  isDirty,
  setIsNavigateModalOpen,
) => {
  if (
    event.target.closest('.sub-menu-item')
    || event.target.closest('.menu-item')
    || event.target.closest('.log-out')
    || event.target.closest('.MuiAutocomplete-listbox')
    || event.target.closest('.sub-menu-item-options')
    || event.target.closest('[aria-label="open drawer"]')
  ) {
    // Show modal only if there are unsaved changes
    if (isDirty) {
      setIsNavigateModalOpen(true);
    }
  }
};

export const handleBeforeUnload = (event, isDirty) => {
  if (isDirty) {
    event.preventDefault();
    event.returnValue = '';
  }
};

// Function to determine CSS classes based on target details
export const getClassNames = (targetItem) => {
  if (
    targetItem
    && targetItem?.currentQuarterAchieved == targetItem?.currentQuarterTarget
    && targetItem?.currentQuarterTarget !== 0
  ) {
    return 'Achieved';
  }
  if (targetItem?.currentQuarterAchieved > targetItem?.currentQuarterTarget) {
    return 'overAchieved';
  }
  if (targetItem?.currentQuarterPending) {
    return 'pending';
  }
  return '';
};

const calculateQuarter = (targets) => {
  let setTargetSum = 0;
  targets.forEach((target) => {
    setTargetSum += target.target || 0;
  });

  return { setTargetSum };
};

export const calculateQuarterCount = (verticals, allTargets) => {
  const quarterTargetsAccumulator = {};
  let totalSetTarget = 0;
  verticals.forEach((vert) => {
    const vertTargets = allTargets[vert.name] || []; // Add default empty array
    vertTargets.forEach((quarter) => {
      const quarterKey = `quarter${quarter.quarter}`;
      if (!quarterTargetsAccumulator[quarterKey]) {
        quarterTargetsAccumulator[quarterKey] = 0;
      }
      const { setTargetSum } = calculateQuarter(quarter.targets);
      quarterTargetsAccumulator[quarterKey] += setTargetSum;
      totalSetTarget += setTargetSum;
    });
  });
  return { quarterTargetsAccumulator, totalSetTarget };
};
